import { logError } from 'fergy-core-react-logging';
import { useContext, useState, type FunctionComponent, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { ERROR_ADD_ITEMS, PRODUCT_CONFIG_SECTION_ID } from '../../../../constants/product';
import { ProductFamilyContext } from '../../../../contexts/product-family/product-family.context';
import { ProductPricedOptionsContext } from '../../../../contexts/product-priced-options/product-priced-options.context';
import { VariantContext } from '../../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES, AnalyticsHelper } from '../../../../helpers/analytics/analytics.helper';
import {
	ADD_TO_CART_BUTTON_TYPE,
	shapePricedOptions
} from '../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { generateATCAnalyticsData } from '../../../../helpers/product-helper/configuration/product-configuration.helper';
import { buildGAConfigureClick } from '../../../../helpers/product-helper/product-analytics.helper';
import { canAddToCart } from '../../../../helpers/product-helper/product.helper';
import { useAddToCartButton } from '../../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { useNewAtcButtonAction } from '../../../../hooks/add-to-cart/add-to-cart.hooks';
import { useNotification } from '../../../../hooks/notification/notification.hooks';
import { useProductStickyNavStore } from '../../../../store/product-sticky-nav/product-sticky-nav.store';
import { scrollToElement } from '../../../../utils/dom/dom.utils';
import { GreyBox } from '../../../common-components/grey-box/grey-box.component';
import { QuantityInput } from '../../../inputs/quantity-input/quantity-input.component';
import { AddToCartButtonFactory } from '../../add-to-cart-button/add-to-cart-button.component';
import { SqFtAddToCart } from '../../sqft-add-to-cart/sqft-add-to-cart.component';
import { SqFtCalculator } from '../../sqft-calculator/sqft-calculator.component';
import { ProductCartDrawer } from '../product-cart-drawer/product-cart-drawer.component';
import { cartQuantityInput } from './product-add-to-cart-section.css';

export const ProductAddToCartSection: FunctionComponent = () => {
	const { productFamily } = useContext(ProductFamilyContext);
	const { selectedVariant } = useContext(VariantContext);
	const {
		mainItemQuantity,
		setMainItemQuantity,
		configurationValid,
		atcLoading,
		setAtcLoading,
		selectedOptions,
		textInputOptions,
		subItems
	} = useContext(ProductPricedOptionsContext);
	const setShouldShowATC = useProductStickyNavStore((state) => state.setShouldShowATC);
	const { ref: addToCartRef, entry: addToCartEntry } = useInView();
	const [openCartDrawer, setOpenCartDrawer] = useState(false);
	const { fireNewAddToCart } = useNewAtcButtonAction();
	const { notifyDanger } = useNotification();
	const [localInputQuantity, setLocalInputQuantity] = useState<number | undefined>(mainItemQuantity);
	const [units, setUnits] = useState<number>(0);

	const { buttonType, addToCartActionStatus } = useAddToCartButton({
		pageName: ANALYTICS_PAGE_NAMES.PRODUCT_DETAILS_PAGE
	});

	const fireATC = () => {
		if (configurationValid && mainItemQuantity) {
			const analyticsData = generateATCAnalyticsData(
				selectedVariant.id,
				mainItemQuantity,
				productFamily,
				undefined,
				ADD_TO_CART_BUTTON_TYPE.DEFAULT
			);
			setAtcLoading(true);
			const pricedOptions = shapePricedOptions(selectedOptions, textInputOptions);

			fireNewAddToCart(
				{
					variantId: selectedVariant.id,
					quantity: mainItemQuantity,
					analyticsData,
					pricedOptions,
					subItems: subItems.map((subItem) => ({
						variantId: subItem.variantId,
						quantity: subItem.quantity,
						pricedOptions: shapePricedOptions(subItem.productOptions, [])
					}))
				},
				productFamily
			)
				.then((data) => {
					if (data.addProductToCart?.newItemIds) {
						setOpenCartDrawer(true);
					}
				})
				.catch(() => {
					notifyDanger(ERROR_ADD_ITEMS);
					setAtcLoading(false);
				})
				.finally(() => {
					setAtcLoading(false);
				});
		} else {
			AnalyticsHelper.track(buildGAConfigureClick()).catch(logError);
			scrollToElement(PRODUCT_CONFIG_SECTION_ID, 55);
		}
	};

	const handleMainItemInputQuantityChange = (newQuanity?: number) => {
		setLocalInputQuantity(newQuanity);
		if (newQuanity !== undefined) {
			setMainItemQuantity(newQuanity);
		}
	};

	useEffect(() => {
		if (localInputQuantity !== undefined) {
			setMainItemQuantity(localInputQuantity);
		}
	}, [localInputQuantity]);

	useEffect(() => {
		if (!addToCartEntry || openCartDrawer) return;
		if (addToCartEntry) {
			// calculation to check if the viewport is below the main ATC button
			// using only the inView boolean, the sticky button will render on mount if product has rec ops
			const viewportBelowMainButton = addToCartEntry.boundingClientRect.y < -addToCartEntry.boundingClientRect.height;
			if (viewportBelowMainButton) {
				setShouldShowATC(true);
			} else {
				setShouldShowATC(false);
			}
		}
	}, [addToCartEntry, openCartDrawer]);

	const shouldRenderAtc = canAddToCart(selectedVariant);

	return (
		<div className={'nl2 nr2 ml0-ns mr0-ns'} ref={addToCartRef}>
			{shouldRenderAtc && (
				<>
					<GreyBox className={'flex items-center mt3 ml0-l mb2 ba b--theme-grey-light pa2-ns'}>
						<div className={`${cartQuantityInput} pr1 pr0-l mr3-l`}>
							<QuantityInput
								quantity={localInputQuantity}
								setQuantity={handleMainItemInputQuantityChange}
								automationHook="cart-quantity"
								isSettled={true}
							/>
						</div>
						<div className="pl1 pl0-l pa0-l w-70 flex-auto-ns">
							<div className="w-100">
								<AddToCartButtonFactory
									buttonType={buttonType}
									addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.DEFAULT}
									handleOnClick={fireATC}
									disabled={!configurationValid || !mainItemQuantity}
									status={addToCartActionStatus}
									isBusy={atcLoading}
								/>
							</div>
						</div>
					</GreyBox>
					{!localInputQuantity && <div className="f7 theme-error pl2-ns pl3">Please enter a quantity greater than 0.</div>}
				</>
			)}
			{selectedVariant?.priceInfo.packaging && (
				<div className={`flex flex-column`}>
					<SqFtAddToCart
						className={`mt3`}
						priceInfo={selectedVariant.priceInfo}
						units={units}
						pageName="Product-PLA"
						onClick={fireATC}
						setQuantity={handleMainItemInputQuantityChange}
					/>
					<SqFtCalculator className={`mt3`} onChange={setUnits} />
				</div>
			)}
			{openCartDrawer && (
				<ProductCartDrawer familyId={productFamily.id} variantId={selectedVariant.id} onClose={() => setOpenCartDrawer(false)} />
			)}
		</div>
	);
};
